// ============================================================================
// MONITOR CAPTURE — Console/Error interception for Debug Monitor (v1.0)
// ============================================================================
// Patches console.error/warn, window.onerror, unhandledrejection.
// Emits MonitorEvents to the store. Safe to remove by disabling flag.
// ============================================================================

import { addEvent } from './MonitorEventStore';

let installed = false;
let origConsoleError = null;
let origConsoleWarn = null;
let origConsoleLog = null;

// Redact sensitive fields from objects
function redact(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const safe = {};
  for (const [k, v] of Object.entries(obj)) {
    if (/key|secret|token|password|authorization/i.test(k)) {
      safe[k] = '[REDACTED]';
    } else {
      safe[k] = v;
    }
  }
  return safe;
}

function formatArgs(args) {
  return args.map(a => {
    if (a instanceof Error) return `${a.message}\n${a.stack || ''}`;
    if (typeof a === 'object') {
      try { return JSON.stringify(redact(a), null, 0).substring(0, 500); } catch (_) { return String(a); }
    }
    return String(a);
  }).join(' ');
}

export function installCapture() {
  if (installed) return;
  installed = true;

  // ── console.error ─────────────────────────────────────────────────
  origConsoleError = console.error;
  console.error = (...args) => {
    origConsoleError.apply(console, args);
    const message = formatArgs(args);
    const errObj = args.find(a => a instanceof Error);
    addEvent({
      level: 'error',
      source: 'frontend',
      category: 'console',
      action: 'CONSOLE_ERROR',
      message: message.substring(0, 500),
      data: errObj ? { stack: errObj.stack } : undefined,
    });
  };

  // ── console.warn ──────────────────────────────────────────────────
  origConsoleWarn = console.warn;
  console.warn = (...args) => {
    origConsoleWarn.apply(console, args);
    const message = formatArgs(args);
    addEvent({
      level: 'warn',
      source: 'frontend',
      category: 'console',
      action: 'CONSOLE_WARN',
      message: message.substring(0, 500),
    });
  };

  // ── console.log (only strategy-tagged) ────────────────────────────
  origConsoleLog = console.log;
  console.log = (...args) => {
    origConsoleLog.apply(console, args);
    const first = String(args[0] || '');
    if (first.startsWith('[STRATEGY]') || first.startsWith('[MONITOR]')) {
      addEvent({
        level: 'debug',
        source: 'frontend',
        category: 'console',
        action: 'CONSOLE_LOG',
        message: formatArgs(args).substring(0, 500),
      });
    }
  };

  // ── window.onerror ────────────────────────────────────────────────
  window.addEventListener('error', (event) => {
    addEvent({
      level: 'error',
      source: 'frontend',
      category: 'ui',
      action: 'RUNTIME_ERROR',
      message: `${event.message || 'Unknown error'} at ${event.filename}:${event.lineno}:${event.colno}`,
      data: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      },
    });
  });

  // ── unhandledrejection ────────────────────────────────────────────
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error
      ? reason.message
      : (typeof reason === 'string' ? reason : 'Unhandled Promise Rejection');
    addEvent({
      level: 'error',
      source: 'frontend',
      category: 'ui',
      action: 'UNHANDLED_REJECTION',
      message: message.substring(0, 500),
      data: {
        stack: reason instanceof Error ? reason.stack : undefined,
      },
    });
  });
}

export function uninstallCapture() {
  if (!installed) return;
  installed = false;
  if (origConsoleError) console.error = origConsoleError;
  if (origConsoleWarn) console.warn = origConsoleWarn;
  if (origConsoleLog) console.log = origConsoleLog;
}
